'use client'

import type { StaticImageData } from 'next/image'

import { cn } from '@/utilities/ui'
import { useTranslations } from 'next-intl'
import NextImage from 'next/image'
import React from 'react'

import type { Media as MediaType } from '@/payload-types'
import type { Props as MediaProps } from '../types'

import { getMediaIntrinsicDimensions } from '@/utilities/mediaImageObject'
import { resolveMediaImageAlt } from '@/utilities/resolveMediaImageAlt'
import { getMediaUrl } from '@/utilities/getMediaUrl'
import { useImageSettings } from '../imageSettingsContext'
import { candidatosDeSrcset, loaderDeCandidatos } from './payloadSizesLoader'

// A base64 encoded image to use as a placeholder while the image is loading
const placeholderBlur =
  'data:image/png;base64,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'

/** `sizes` por defeito quando o pai não define (evita valores inválidos tipo `1536w`). */
const DEFAULT_IMAGE_SIZES =
  '(max-width: 768px) 100vw, (max-width: 1280px) min(92vw, 1100px), 1200px'

export const ImageMedia: React.FC<MediaProps> = (props) => {
  const t = useTranslations('Media')
  const {
    alt: altFromProps,
    fill,
    pictureClassName,
    imgClassName,
    imgProps,
    priority,
    fetchPriority,
    resource,
    size: sizeFromProps,
    src: srcFromProps,
    loading: loadingFromProps,
    unoptimized = false,
  } = props

  let width: number | undefined
  let height: number | undefined
  let alt: string | undefined = altFromProps
  let src: StaticImageData | string = srcFromProps || ''

  if (!src && resource && typeof resource === 'object') {
    const { height: fullHeight, url, width: fullWidth } = resource as MediaType

    const dims = getMediaIntrinsicDimensions(resource as MediaType)
    width = fullWidth || dims.width
    height = fullHeight || dims.height

    alt =
      typeof altFromProps === 'string' && altFromProps.trim().length > 0
        ? altFromProps.trim()
        : resolveMediaImageAlt(resource as MediaType)

    const cacheTag = resource.updatedAt
    src = getMediaUrl(url, cacheTag)
  }

  /** Garante dimensões para `next/image` quando o documento vem sem width/height no nível raiz. */
  if (!fill && resource && typeof resource === 'object' && (!width || !height)) {
    const d = getMediaIntrinsicDimensions(resource as MediaType)
    width = width || d.width
    height = height || d.height
  }

  const loading = loadingFromProps ?? (!priority ? 'lazy' : undefined)
  const sizes = sizeFromProps ?? DEFAULT_IMAGE_SIZES
  const resolvedFetchPriority = fetchPriority ?? (priority ? 'high' : undefined)

  const displayAlt =
    (typeof alt === 'string' && alt.trim().length > 0 ? alt.trim() : '') || t('altFallback')

  const mime =
    resource && typeof resource === 'object' ? ((resource as MediaType).mimeType ?? '') : ''
  const srcString = typeof src === 'string' ? src : ''
  // A mídia do Payload fica em R2 público ATRÁS da Cloudflare, que dá 403 no fetch SERVER-SIDE do
  // otimizador do Next (/_next/image) → a imagem caía no fallback (template). URL absoluta aqui é
  // sempre mídia R2 (ImageMedia só renderiza uploads) → servir SEM otimizar: o browser carrega
  // direto da CDN (funciona). Ver project_imagem_next_optimizer_r2_403.
  const isRemoteMedia = /^https?:\/\//i.test(srcString)
  const isSvgLike =
    unoptimized ||
    mime === 'image/svg+xml' ||
    mime === 'application/xml' ||
    /\.svg(\?|#|$)/i.test(srcString)

  const { className: imgPropsClassName, ...restImgProps } = imgProps ?? {}

  /**
   * SRCSET a partir dos tamanhos que o Payload já gerou (performance, 19/08/2026 — ver
   * `payloadSizesLoader.ts`). Só liga quando há mídia remota NÃO-SVG com pelo menos um tamanho menor
   * que o original; fora disso o caminho é o de sempre (`unoptimized`, sem srcset). Sem `/_next/image`.
   */
  const candidatos = React.useMemo(
    () =>
      isRemoteMedia && !isSvgLike && resource && typeof resource === 'object'
        ? candidatosDeSrcset(resource as MediaType, srcString)
        : [],
    [isRemoteMedia, isSvgLike, resource, srcString],
  )
  const loaderPayload = React.useMemo(
    () => (candidatos.length >= 2 ? loaderDeCandidatos(candidatos) : undefined),
    [candidatos],
  )

  // Config global de imagens (aba «Imagens» do Identidade do site): fundo de loading + fallback onError.
  const imageSettings = useImageSettings()
  const grayLoading = imageSettings.loadingBackground === 'gray' && !isSvgLike
  const [errored, setErrored] = React.useState(false)
  const [loaded, setLoaded] = React.useState(false)

  // onError → cai na imagem fallback configurada (uma vez; evita loop se o próprio fallback falhar).
  const effectiveSrc =
    errored && imageSettings.fallbackUrl && src !== imageSettings.fallbackUrl
      ? imageSettings.fallbackUrl
      : src

  // 'gray' substitui o blur por fundo cinza + fade-in (respeitando prefers-reduced-motion).
  const useBlur = !isSvgLike && !grayLoading
  // O fallback (onError) é URL local — volta ao caminho padrão, sem o loader do Payload.
  const usarLoaderPayload = loaderPayload != null && !errored

  return (
    <div
      className={cn(
        fill && 'relative block h-full min-h-full w-full',
        grayLoading && !loaded && 'bg-(--theme-elevation-100)',
        pictureClassName,
      )}
    >
      <NextImage
        {...restImgProps}
        alt={displayAlt}
        className={cn(
          imgClassName,
          imgPropsClassName,
          grayLoading && 'transition-opacity duration-300 motion-reduce:transition-none',
          grayLoading && !loaded && 'opacity-0',
        )}
        decoding="async"
        fetchPriority={resolvedFetchPriority}
        fill={fill}
        height={!fill ? height : undefined}
        placeholder={useBlur ? 'blur' : 'empty'}
        blurDataURL={useBlur ? placeholderBlur : undefined}
        priority={priority}
        loader={usarLoaderPayload ? loaderPayload : undefined}
        quality={isSvgLike || isRemoteMedia ? undefined : 100}
        loading={loading}
        sizes={sizes}
        src={effectiveSrc}
        onError={() => setErrored(true)}
        onLoad={() => setLoaded(true)}
        unoptimized={usarLoaderPayload ? false : isSvgLike || isRemoteMedia}
        width={!fill ? width : undefined}
      />
    </div>
  )
}
